/**
 * CollectionsService - Service for fetching collection data from Voyager API
 *
 * This service handles fetching facet counts and collection metadata
 * to populate the Collections block dynamically.
 */

/* eslint-disable no-console */
import { getLocalStorage } from 'nsdi-volto-frontend/utils/browserStorage';
import { environment } from 'nsdi-volto-frontend/config/environments';

// Simple logging utilities - use console as fallback
const logDebug = (context: string, message: string, data?: any) => {
  if (process.env.NODE_ENV === 'development') {
    console.debug(`[${context}] ${message}`, data || '');
  }
};

const logWarn = (context: string, message: string, data?: any) => {
  console.warn(`[${context}] ${message}`, data || '');
};

const logError = (context: string, message: string, data?: any) => {
  console.error(`[${context}] ${message}`, data || '');
};

// API Configuration
export interface CollectionsApiConfig {
  baseUrl: string;
  configId: string;
}

export const DEFAULT_COLLECTIONS_CONFIG: CollectionsApiConfig = {
  baseUrl: environment.voyager.baseUrl,
  configId: environment.voyager.configId,
};

// Collection item with source mapping
export interface CollectionDefinition {
  id: string;
  title: string;
  displayTitle?: string;
  // Source type: 'facet' for facet fields, 'query' for custom queries
  sourceType: 'facet' | 'query';
  // For facet type: the facet field name
  facetField?: string;
  // For facet type: the value to look up in that facet
  facetValue?: string;
  // For query type: the filter query (fq parameter)
  filterQuery?: string;
  // Optional link
  link?: string;
}

// Pre-defined collection definitions with Voyager catalog links
export const DEFAULT_COLLECTION_DEFINITIONS: CollectionDefinition[] = [
  /*
  {
    id: 'dp23-ngda',
    title: 'Showcase | DP23 NGDA',
    sourceType: 'query',
    filterQuery: 'tag_tags:DP23',
    link: 'https://ogc-sdi.voyagersearch.com/navigo/search?sort=fi_score%20desc&f.grp_Data_Theme.facet.mincount=0&f.grp_Data_Theme.facet.sort=index&f.grp_Sub%5C-Region.facet.sort=index&f.grp_Geography.facet.sort=index&f.grp_State.facet.sort=index&f.grp_County.facet.sort=index&f.grp_Bureau_%5C%2F_Department.facet.sort=index&f.fs_platform.facet.sort=index&fq=tag_tags:DP23&disp=D187992491DF&basemap=ESRI%20Street%20Map&view=card&sortdir=desc',
  },
  {
    id: 'wildfire-maui-2023',
    title: 'Showcase | Wildfire | Maui 2023',
    sourceType: 'query',
    filterQuery: 'fs_event:202308_Wildfire_Maui',
    link: 'https://ogc-sdi.voyagersearch.com/navigo/search?sort=fd_acquisition_date%20asc&f.grp_Data_Theme.facet.mincount=0&f.grp_Data_Theme.facet.sort=index&f.grp_Sub%5C-Region.facet.sort=index&f.grp_Geography.facet.sort=index&f.grp_State.facet.sort=index&f.grp_County.facet.sort=index&f.grp_Bureau_%5C%2F_Department.facet.sort=index&f.fs_platform.facet.sort=index&fq=fs_event:202308_Wildfire_Maui&disp=D187992491DF&basemap=ESRI%20Street%20Map&view=card&sortdir=asc',
  },
  {
    id: 'nasa-disasters',
    title: 'NASA Disasters Mapping Portal',
    sourceType: 'facet',
    facetField: 'grp_Catalog',
    facetValue: 'NASA Disasters Mapping Portal',
    link: 'https://ogc-sdi.voyagersearch.com/navigo/search?sort=score%20desc&fq=grp_Catalog:NASA%5C%20Disasters%5C%20Mapping%5C%20Portal&disp=D187992491DF&basemap=ESRI%20Street%20Map&view=card&sortdir=desc',
  },
  {
    id: 'hifld-open',
    title: 'HIFLD Open',
    sourceType: 'facet',
    facetField: 'grp_Catalog',
    facetValue: 'HIFLD Open',
    link: 'https://ogc-sdi.voyagersearch.com/navigo/search?sort=fi_score%20asc&fq=grp_Catalog:HIFLD%5C%20Open&fq=format:application%5C%2Fvnd.esri.service.layer.record&disp=D187992491DF&basemap=ESRI%20Street%20Map&view=card&sortdir=asc',
  },
  {
    id: 'hdds',
    title: 'Hazards Data Distribution System (HDDS)',
    sourceType: 'facet',
    facetField: 'grp_Catalog',
    facetValue: 'Hazards Data Distribution System (HDDS)',
    link: 'https://ogc-sdi.voyagersearch.com/navigo/search?sort=fd_acquisition_date%20desc&fq=grp_Catalog:Hazards%20Data%20Distribution%20System%20(HDDS)&disp=D187992491DF&basemap=ESRI%20Street%20Map&view=card&sortdir=desc',
  },
  {
    id: 'geoglam',
    title: 'GEOGLAM',
    sourceType: 'query',
    filterQuery: 'organization:GEOGLAM',
    link: 'https://ogc-sdi.voyagersearch.com/navigo/search?sort=fi_score%20desc&f.grp_Data_Theme.facet.mincount=0&f.grp_Data_Theme.facet.sort=index&f.grp_Sub%5C-Region.facet.sort=index&f.grp_Geography.facet.sort=index&f.grp_State.facet.sort=index&f.grp_County.facet.sort=index&f.city.facet.sort=index&f.grp_Bureau_%5C%2F_Department.facet.sort=index&f.fs_platform.facet.sort=index&fq=organization:GEOGLAM&disp=default&basemap=ESRI%20Street%20Map&view=card&sortdir=desc',
  },
  {
    id: 'amerigeo',
    title: 'AmeriGeo',
    sourceType: 'facet',
    facetField: 'grp_Catalog',
    facetValue: 'AmeriGeo',
    link: 'https://ogc-sdi.voyagersearch.com/navigo/search?sort=fi_score%20asc&fq=grp_Catalog:AmeriGeo&disp=D188B5C37E5E&basemap=ESRI%20Street%20Map&view=card&sortdir=asc',
  },*/
];

// Response interface for collection counts
export interface CollectionCountResult {
  id: string;
  title: string;
  itemCount: number;
  link?: string;
  error?: string;
}

/**
 * Get the Voyager API search URL from config
 */
const getSearchUrl = (config: CollectionsApiConfig): string => {
  // Clean URL - remove trailing slash and /solr/v0/select if present, then add it back
  const baseUrl = config.baseUrl
    .replace(/\/$/, '')
    .replace(/\/solr\/v0\/select$/, '');
  return `${baseUrl}/solr/v0/select`;
};

/**
 * Parse facet field array into a map of value -> count
 * Facet arrays come as [value1, count1, value2, count2, ...]
 */
const parseFacetArray = (
  facetArray: (string | number)[],
): Map<string, number> => {
  const map = new Map<string, number>();
  for (let i = 0; i < facetArray.length; i += 2) {
    const value = facetArray[i] as string;
    const count = facetArray[i + 1] as number;
    map.set(value, count);
  }
  return map;
};

/**
 * Fetch facet counts from Voyager API
 * This retrieves all facet data in a single request
 */
export const fetchFacetCounts = async (
  config: CollectionsApiConfig = DEFAULT_COLLECTIONS_CONFIG,
): Promise<Map<string, Map<string, number>>> => {
  const searchUrl = getSearchUrl(config);

  const params = new URLSearchParams({
    'voyager.config.id': config.configId,
    rows: '0',
    facet: 'true',
    'facet.mincount': '1',
    'facet.limit': '100',
    wt: 'json',
  });

  // Add facet fields
  const facetFields = [
    'grp_Data_Theme',
    'grp_Catalog',
    'grp_Organization',
    'grp_Agency',
    'grp_data_source',
  ];

  facetFields.forEach((field) => {
    params.append('facet.field', field);
  });

  try {
    const response = await fetch(`${searchUrl}?${params.toString()}`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    const facetCounts = new Map<string, Map<string, number>>();

    if (data.facet_counts?.facet_fields) {
      for (const [fieldName, values] of Object.entries(
        data.facet_counts.facet_fields,
      )) {
        facetCounts.set(
          fieldName,
          parseFacetArray(values as (string | number)[]),
        );
      }
    }

    logDebug('CollectionsService', 'Fetched facet counts', {
      fieldsCount: facetCounts.size,
    });

    return facetCounts;
  } catch (error) {
    logError('CollectionsService', 'Failed to fetch facet counts', error);
    throw error;
  }
};

/**
 * Fetch count for a single query-based collection
 */
export const fetchQueryCount = async (
  filterQuery: string,
  config: CollectionsApiConfig = DEFAULT_COLLECTIONS_CONFIG,
): Promise<number> => {
  const searchUrl = getSearchUrl(config);

  const params = new URLSearchParams({
    'voyager.config.id': config.configId,
    rows: '0',
    fq: filterQuery,
    wt: 'json',
  });

  try {
    const response = await fetch(`${searchUrl}?${params.toString()}`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.response?.numFound || 0;
  } catch (error) {
    logWarn(
      'CollectionsService',
      `Failed to fetch count for query: ${filterQuery}`,
      error,
    );
    return 0;
  }
};

/**
 * Fetch count from a Voyager search query string (URL parameters)
 * This parses the query string and extracts the count from the API response
 */
export const fetchCountFromQueryString = async (
  queryString: string,
  baseUrl: string = DEFAULT_COLLECTIONS_CONFIG.baseUrl,
): Promise<number> => {
  // Clean up the query string - remove leading ? if present
  const cleanQuery = queryString.trim().replace(/^\?/, '');

  // Build the API URL - clean base URL and add solr endpoint
  const apiBaseUrl = baseUrl
    .replace(/\/$/, '')
    .replace(/\/solr\/v0\/select$/, '');
  const solrUrl = `${apiBaseUrl}/solr/v0/select`;

  // Parse the query params to extract fq (filter query) parameters
  const params = new URLSearchParams(cleanQuery);

  // Build a new request just for getting the count (rows=0)
  const countParams = new URLSearchParams();
  countParams.set('rows', '0');
  countParams.set('wt', 'json');

  // Copy over filter queries (fq) - these determine what we're counting
  const fqValues = params.getAll('fq');
  fqValues.forEach((fq) => countParams.append('fq', fq));

  // Copy over the config ID if present, or use default
  const configId = params.get('disp') || params.get('voyager.config.id');
  if (configId) {
    countParams.set('voyager.config.id', configId);
  }

  // Copy q (query) if present
  const q = params.get('q');
  if (q) {
    countParams.set('q', q);
  }

  try {
    const response = await fetch(`${solrUrl}?${countParams.toString()}`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    const count = data.response?.numFound || 0;

    logDebug('CollectionsService', `Fetched count for query`, {
      query: cleanQuery.substring(0, 100) + '...',
      count,
    });

    return count;
  } catch (error) {
    logWarn(
      'CollectionsService',
      `Failed to fetch count from query string`,
      error,
    );
    return 0;
  }
};

// Cache interface for storing counts
interface CachedCount {
  count: number;
  timestamp: number;
}

// In-memory cache for collection counts
const countCache = new Map<string, CachedCount>();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes cache TTL

/**
 * Generate a cache key from query string
 */
const getCacheKey = (queryString: string, baseUrl: string): string => {
  return `${baseUrl}:${queryString}`;
};

/**
 * Fetch count with caching support
 * Uses in-memory cache with configurable TTL, and optionally persists to localStorage
 */
export const fetchCountFromQueryStringCached = async (
  queryString: string,
  baseUrl: string = DEFAULT_COLLECTIONS_CONFIG.baseUrl,
  cacheTtlMs: number = CACHE_TTL_MS,
): Promise<number> => {
  const cacheKey = getCacheKey(queryString, baseUrl);
  const now = Date.now();

  // Check in-memory cache first
  const cached = countCache.get(cacheKey);
  if (cached && now - cached.timestamp < cacheTtlMs) {
    logDebug('CollectionsService', 'Using cached count', {
      cacheKey,
      count: cached.count,
    });
    return cached.count;
  }

  // Check localStorage for persisted cache
  try {
    const storedCache = getLocalStorage()?.getItem(`voyager_count_${cacheKey}`);
    if (storedCache) {
      const parsed: CachedCount = JSON.parse(storedCache);
      if (now - parsed.timestamp < cacheTtlMs) {
        // Update in-memory cache
        countCache.set(cacheKey, parsed);
        logDebug('CollectionsService', 'Using localStorage cached count', {
          count: parsed.count,
        });
        return parsed.count;
      }
    }
  } catch (e) {
    // Ignore localStorage errors
  }

  // Fetch fresh count
  const count = await fetchCountFromQueryString(queryString, baseUrl);

  // Update caches
  const cacheEntry: CachedCount = { count, timestamp: now };
  countCache.set(cacheKey, cacheEntry);

  // Persist to localStorage
  try {
    getLocalStorage()?.setItem(
      `voyager_count_${cacheKey}`,
      JSON.stringify(cacheEntry),
    );
  } catch (e) {
    // Ignore localStorage errors (quota exceeded, etc.)
  }

  return count;
};

/**
 * Fetch counts for multiple static collections
 */
export const fetchStaticCollectionCounts = async (
  collections: Array<{
    id?: string;
    title: string;
    link?: string;
    itemCount?: number;
  }>,
  baseUrl: string = DEFAULT_COLLECTIONS_CONFIG.baseUrl,
): Promise<
  Array<{ id: string; title: string; link?: string; itemCount: number }>
> => {
  const results = await Promise.all(
    collections.map(async (collection, index) => {
      const id = collection.id || `collection-${index}`;

      // If no link, return with existing count or 0
      if (!collection.link) {
        return {
          id,
          title: collection.title,
          link: collection.link,
          itemCount: collection.itemCount || 0,
        };
      }

      // If itemCount is already set and > 0, use it (manual override)
      if (collection.itemCount && collection.itemCount > 0) {
        return {
          id,
          title: collection.title,
          link: collection.link,
          itemCount: collection.itemCount,
        };
      }

      // Fetch count from API
      const itemCount = await fetchCountFromQueryStringCached(
        collection.link,
        baseUrl,
      );

      return {
        id,
        title: collection.title,
        link: collection.link,
        itemCount,
      };
    }),
  );

  return results;
};

/**
 * Fetch counts for all collection definitions
 * Optimizes by fetching facet data once and making individual queries for query-based collections
 */
export const fetchAllCollectionCounts = async (
  definitions: CollectionDefinition[] = DEFAULT_COLLECTION_DEFINITIONS,
  config: CollectionsApiConfig = DEFAULT_COLLECTIONS_CONFIG,
): Promise<CollectionCountResult[]> => {
  const results: CollectionCountResult[] = [];

  try {
    // Fetch all facet data in one request
    const facetCounts = await fetchFacetCounts(config);

    // Process each collection definition
    const promises = definitions.map(
      async (def): Promise<CollectionCountResult> => {
        try {
          let itemCount = 0;

          if (def.sourceType === 'facet' && def.facetField && def.facetValue) {
            // Look up count from facet data
            const fieldCounts = facetCounts.get(def.facetField);
            itemCount = fieldCounts?.get(def.facetValue) || 0;
          } else if (def.sourceType === 'query' && def.filterQuery) {
            // Fetch count via query
            itemCount = await fetchQueryCount(def.filterQuery, config);
          }

          return {
            id: def.id,
            title: def.displayTitle || def.title,
            itemCount,
            link: def.link,
          };
        } catch (error) {
          logWarn(
            'CollectionsService',
            `Failed to fetch count for ${def.title}`,
            error,
          );
          return {
            id: def.id,
            title: def.displayTitle || def.title,
            itemCount: 0,
            link: def.link,
            error: 'Failed to fetch count',
          };
        }
      },
    );

    const resolvedResults = await Promise.all(promises);
    results.push(...resolvedResults);
  } catch (error) {
    logError('CollectionsService', 'Failed to fetch collection counts', error);
    // Return definitions with zero counts on error
    return definitions.map((def) => ({
      id: def.id,
      title: def.displayTitle || def.title,
      itemCount: 0,
      link: def.link,
      error: 'Failed to fetch counts',
    }));
  }

  return results;
};

/**
 * React hook for fetching collection counts
 */
export const useCollectionCounts = (
  definitions: CollectionDefinition[] = DEFAULT_COLLECTION_DEFINITIONS,
  config: CollectionsApiConfig = DEFAULT_COLLECTIONS_CONFIG,
) => {
  // This is a placeholder - the actual hook is in useCollectionCounts.ts
  // to avoid importing React in this service file
  return { collections: [], loading: true, error: null };
};
